import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { ChangeEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import NumberField from '../NumberField';
import { type OverheadCostLineItem, validate as validateOverheadCostLineItem } from '../../models/OverheadCostLineItem';
import { FormControlLabel, Switch } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickerValue } from '@mui/x-date-pickers/internals';
import dayjs from 'dayjs';

export const newOverheadCostLineItem = {
  id: 0,
  description: '',
  totalCost: 0,
  isActive: true
} as OverheadCostLineItem;

export interface OverheadCostLineItemFormState {
  values: Partial<Omit<OverheadCostLineItem, 'id'>>;
  errors: Partial<Record<keyof OverheadCostLineItemFormState['values'], string>>;
}

export interface OverheadCostLineItemFormProps {
  initialValue: OverheadCostLineItem;
  initialErrors?: Partial<Record<keyof OverheadCostLineItemFormState['values'], string>>;
  onReset?: (formValues: Partial<OverheadCostLineItemFormState['values']>) => void;
  onFormValuesUpdate?: (formValues: Partial<OverheadCostLineItemFormState['values']>) => void;
}

export default function OverheadCostLineItemForm(props: OverheadCostLineItemFormProps) {
  const {
    initialValue,
    initialErrors = {},
    onReset = () => { },
    onFormValuesUpdate = () => { }
  } = props;

  const [errors, setErrors] = useState<Partial<Record<keyof OverheadCostLineItemFormState['values'], string>>>({});

  const handleFormFieldChange = useCallback(
    (name: keyof OverheadCostLineItemFormState['values'], value: FormFieldValue) => {
      const validateField = (values: Partial<OverheadCostLineItemFormState['values']>) => {
        const { issues } = validateOverheadCostLineItem(values);

        const issue = issues?.find((issue) => issue.path?.[0] === name);

        if (issue) {
          setErrors({
            ...errors,
            [name]: issue.message,
          });
          return false;
        }

        return true;
      };

      const newFormValues = { ...initialValue, [name]: value };

      const valid = validateField(newFormValues);

      if (valid) {
        onFormValuesUpdate(newFormValues);
      }
    },
    [initialValue, errors, onFormValuesUpdate],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      handleFormFieldChange(
        fieldName as keyof OverheadCostLineItemFormState['values'],
        value,
      );
    },
    [handleFormFieldChange],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleFormFieldChange(
        event.target.name as keyof OverheadCostLineItemFormState['values'],
        event.target.value,
      );
    },
    [handleFormFieldChange],
  );

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleFormFieldChange(
        event.target.name as keyof OverheadCostLineItemFormState['values'],
        event.target.checked
      );
    },
    [handleFormFieldChange],
  );

  const handleDateFieldChange = useCallback(
    (value: PickerValue, name: keyof OverheadCostLineItemFormState['values']) => {
      handleFormFieldChange(
        name,
        value,
      );
    },
    [handleFormFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(initialValue || newOverheadCostLineItem);
    }
  }, [initialValue, onReset]);

  return (
    <Box
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 3 }} sx={{ display: 'flex' }}>
            <TextField
              required
              value={initialValue.description || ''}
              onChange={handleTextFieldChange}
              slotProps={{ inputLabel: { shrink: true } }}
              name="description"
              label="Description"
              error={!!errors.description || !!initialErrors.description}
              helperText={(errors.description || initialErrors.description) ?? ''}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              required
              value={initialValue.totalCost || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'totalCost')}
              name="totalCost"
              label="Total Cost"
              error={!!errors.totalCost || !!initialErrors.totalCost}
              helperText={(errors.totalCost || initialErrors.totalCost) ?? ''}
              fullWidth
              currencyField
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={initialValue.annualCostDivisor || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'annualCostDivisor')}
              disabled={!!initialValue.annualCostMultiplier}
              name="annualCostDivisor"
              label="Annual Cost Divisor"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={initialValue.annualCostMultiplier || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'annualCostMultiplier')}
              disabled={!!initialValue.annualCostDivisor}
              name="annualCostMultiplier"
              label="Annual Cost Multiplier"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }} sx={{ display: 'flex' }}>
            <TextField
              value={initialValue.recipient || ''}
              onChange={handleTextFieldChange}
              slotProps={{ inputLabel: { shrink: true } }}
              name="recipient"
              label="Recipient"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 5, sm: 2 }} sx={{ display: 'flex' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={initialValue.dateTimePurchased ? dayjs(initialValue.dateTimePurchased) : null}
                onChange={(event) => handleDateFieldChange(event, 'dateTimePurchased')}
                label="Date Purchased"
                name="dateTimePurchased"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: {
                      shrink: true
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 5, sm: 2 }} sx={{ display: 'flex' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={initialValue.dateTimeDue ? dayjs(initialValue.dateTimeDue) : null}
                onChange={(event) => handleDateFieldChange(event, 'dateTimeDue')}
                label="Date Due"
                name="dateTimeDue"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: {
                      shrink: true
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 5, sm: 2 }} sx={{ display: 'flex' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={initialValue.dateTimePaid ? dayjs(initialValue.dateTimePaid) : null}
                onChange={(event) => handleDateFieldChange(event, 'dateTimePaid')}
                label="Date Paid"
                name="dateTimePaid"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: {
                      shrink: true
                    }
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }} sx={{ display: 'flex' }}>
            <TextField
              value={initialValue.notes || ''}
              onChange={handleTextFieldChange}
              slotProps={{ inputLabel: { shrink: true } }}
              name="notes"
              label="Notes"
              fullWidth
            />
          </Grid>
          <Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={initialValue.isActive}
                  name="isActive"
                  onChange={handleCheckboxFieldChange}
                  slotProps={{
                    input: { 'aria-label': 'controlled' },
                  }} />}
              label="Active?" />
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
}
